import { lazy, Suspense } from 'react';
import Layout from "./Layout.jsx";
import { PWAUpdatePrompt } from '../components/PWAUpdatePrompt';

import Dashboard from "./Dashboard";

import Home from "./Home";

import Admin from "./Admin";

import Cases from "./Cases";

import CaseDetail from "./CaseDetail";

import Profile from "./Profile";

import UserManagement from "./UserManagement";

import CompanySettings from "./CompanySettings";

import UserDetail from "./UserDetail";

import Leads from "./Leads";

import LeadDetail from "./LeadDetail";

import SecuritySettings from "./SecuritySettings";

import CustomerDetail from "./CustomerDetail";

import JotformIntegration from "./JotformIntegration";

import ModuleSettings from "./ModuleSettings";

import Contacts from "./Contacts";

import ContactDetail from "./ContactDetail";

import Deals from "./Deals";

import DealDetail from "./DealDetail";

import Finance from "./Finance";

import SetupSupabase from "./SetupSupabase";

import QuickSetupCaseTypes from "./QuickSetupCaseTypes";

import CasesServicesSettings from "./CasesServicesSettings";

import SetupCaseRoles from "./SetupCaseRoles";

import CollectionDashboard from "./finance/CollectionDashboard";

import WhatsAppSettings from "./WhatsAppSettings";

import TicketTemplatesSettings from "./TicketTemplatesSettings";

import MilestoneTemplatesSettings from "./MilestoneTemplatesSettings";

import Tasks from "./Tasks";
import { useLocation as useLocationForTasks } from "react-router-dom";

// Mantém taskId e demais parâmetros ao redirecionar /tasks → Agenda (lista)
function TasksRedirect() {
  const { search } = useLocationForTasks();
  const params = new URLSearchParams(search);
  params.set("view", "list");
  return <Navigate to={`/agenda?${params.toString()}`} replace />;
}

import WhatsApp from "./WhatsApp.tsx";

import Auth from "./Auth";
import AuthCallback from "./AuthCallback";

import WhatsAppConnect from "./WhatsAppConnect";

import ProductFeedback from "./ProductFeedback";

import DepartmentsSettings from "./DepartmentsSettings";

import SystemRolesSettings from "./SystemRolesSettings";

import AssignmentRulesSettings from "./AssignmentRulesSettings";

import ReactivationSettings from "./ReactivationSettings";
import ReferralRequestSettings from "./ReferralRequestSettings";

import ZohoMapping from "./ZohoMapping";

import ZohoImport from "./ZohoImport";


import WhatsAppMessageTemplatesSettings from "./WhatsAppMessageTemplatesSettings";

import WhatsAppTemplateDetail from "./WhatsAppTemplateDetail";

import UIPersonalizationSettings from "./UIPersonalizationSettings";

import SuvSignSettings from "./SuvSignSettings";

import FacebookLeadsMigration from "./FacebookLeadsMigration";

import FacebookLeadsSync from "./FacebookLeadsSync";

import FacebookSettings from "./FacebookSettings";

import CatalogSettings from "./CatalogSettings";

import AuditLogs from "./AuditLogs";

import Calls from "./Calls";

import ContactNotesImport from "./ContactNotesImport";

import DealNotesImport from "./DealNotesImport";

import SDRDashboard from "./SDRDashboard";
import ConsultantDashboard from "./ConsultantDashboard";
import DashboardSettings from "./DashboardSettings";
import MergeContacts from "./MergeContacts";
import MergeDeals from "./MergeDeals";
import VideoRingSettings from "./VideoRingSettings";
import CallRingSettings from "./CallRingSettings";
import CompanyCallRingSettings from "./CompanyCallRingSettings";
import CompanyPhoneNumbers from "./settings/CompanyPhoneNumbers";
import PowerDialerSettings from "./settings/PowerDialerSettings";

import SalesGoalsSettings from "./SalesGoalsSettings";
import KnowledgeBase from "./KnowledgeBase";
import KommoImport from "./KommoImport";
import KommoSync from "./KommoSync";
import AdminKommoImport from "./AdminKommoImport";
import Reports from "./Reports";
import SalesReport from "./SalesReport";
import CommissionReport from "./CommissionReport";
import ProcessosReport from "./ProcessosReport";
import ServiceDetail from "./ServiceDetail";
import Onboarding from "./Onboarding";
import OnboardingDetail from "./OnboardingDetail";
import FormTemplatesSettings from "./FormTemplatesSettings";
import ItBacklog from "./ItBacklog";
import PerformanceDashboard from "./admin/PerformanceDashboard";
import QualityAnalysisRunner from "./admin/QualityAnalysisRunner";
import MeetingHealthDashboard from "./admin/MeetingHealthDashboard";
import PowerDialerDashboard from "./admin/PowerDialerDashboard";
import CustomerService from "./CustomerService";
import { CustomerServiceRoute } from "@/components/routing/CustomerServiceRoute";
import ManagerDashboard from "./ManagerDashboard";
import AnnouncementsSettings from "./AnnouncementsSettings";
import Announcements from "./Announcements";
import NotificationSettings from "./NotificationSettings";
import DataSettings from "./DataSettings";
import ConnectionCorrectionSettings from "./settings/ConnectionCorrectionSettings";
import SLAConfigSettings from "./SLAConfigSettings";
import QualificationOptionsSettings from "./QualificationOptionsSettings";
import WebhookManagement from "./WebhookManagement";
import MeetRoom from "./MeetRoom";
import OutboundPanel from "./OutboundPanel";
import DialerSessionResult from "./DialerSessionResult";
import DialerSessions from "./DialerSessions";
import { PowerDialerGlobalConsole } from "@/components/outbound/PowerDialerGlobalConsole";
import MyDashboard from "./MyDashboard";
import Agenda from "./Agenda";
import Lists from "./Lists";
import ListDetail from "./ListDetail";

// Settings pages with lazy loading
const SettingsLayout = lazy(() => import('./settings/SettingsLayout'));
const TeamSettings = lazy(() => import('./settings/TeamSettings'));
const CommunicationSettings = lazy(() => import('./settings/CommunicationSettings'));
const OperationsSettings = lazy(() => import('./settings/OperationsSettings'));
const CasesSettings = lazy(() => import('./settings/CasesSettings'));
const FinancialSettings = lazy(() => import('./settings/FinancialSettings'));
const IntegrationsSettings = lazy(() => import('./settings/IntegrationsSettings'));
const SystemSettings = lazy(() => import('./settings/SystemSettings'));
const NewCollaboratorOnboarding = lazy(() => import('./settings/NewCollaboratorOnboarding'));

// Finance pages
import { 
  FinanceDashboard,
  Reconciliation,
  CashFlow,
  BankAccounts, 
  BankAccountTransactions,
  Expenses, 
  Receivables,
  FinanceReportsMenu,
  DreReport,
  SellerPerformanceReport,
  BlueVizaConversionsReport,
  PaymentAuditReport,
  WeeklyReceivablesReport,
  CohortAnalysisReport,
  Budget,
  BudgetVsActual,
  TransactionsReport,
  StripeMirror,
  ClosePeriod,
  FinanceSettings,
  Employees,
  RecurringExpenses,
  KpiTargets,
  ChartOfAccounts,
  ExchangeRates,
  CostAnalysis,
  StripeCleanup,
  CommissionRules
} from "./finance/index";

// People pages
import { PeopleDashboard, EmployeesPage } from "./people/index";

import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import AdminRoute from '../components/AdminRoute';
import MobileRoutes from './mobile/MobileRoutes';
import MobilePreview from './mobile/MobilePreview';
import StageMobileAccess from './StageMobileAccess';
import StageExit from './StageExit';
import { MobileUIProvider } from '@/contexts/MobileUIContext';

const PAGES = {
    
    Dashboard: Dashboard,
    
    Admin: Admin,
    
    Cases: Cases,
    
    CaseDetail: CaseDetail,
    
    Profile: Profile,
    
    UserManagement: UserManagement,
    
    CompanySettings: CompanySettings,
    
    UserDetail: UserDetail,
    
    Leads: Leads,
    
    LeadDetail: LeadDetail,
    
    SecuritySettings: SecuritySettings,
    
    CustomerDetail: CustomerDetail,
    
    JotformIntegration: JotformIntegration,
    
    ModuleSettings: ModuleSettings,
    
    Contacts: Contacts,
    
    ContactDetail: ContactDetail,
    
    Deals: Deals,
    
    Layout: Layout,
    
    DealDetail: DealDetail,
    
    Finance: Finance,
    
    SetupSupabase: SetupSupabase,
    
    QuickSetupCaseTypes: QuickSetupCaseTypes,
    
    CasesServicesSettings: CasesServicesSettings,
    
    SetupCaseRoles: SetupCaseRoles,
    
    WhatsAppSettings: WhatsAppSettings,
    
    TicketTemplatesSettings: TicketTemplatesSettings,
    
    MilestoneTemplatesSettings: MilestoneTemplatesSettings,
    
    Tasks: Tasks,
    
    WhatsApp: WhatsApp,
    
    Auth: Auth,
    
    ProductFeedback: ProductFeedback,
    
    DepartmentsSettings: DepartmentsSettings,
    
    SystemRolesSettings: SystemRolesSettings,
    
    AssignmentRulesSettings: AssignmentRulesSettings,
    
    ReactivationSettings: ReactivationSettings,
    
    ReferralRequestSettings: ReferralRequestSettings,
    
    ZohoMapping: ZohoMapping,
    
    ZohoImport: ZohoImport,
    
    WhatsAppMessageTemplatesSettings: WhatsAppMessageTemplatesSettings,
    
    UIPersonalizationSettings: UIPersonalizationSettings,
    
    SuvSignSettings: SuvSignSettings,
    
    FacebookLeadsMigration: FacebookLeadsMigration,
    
    FacebookLeadsSync: FacebookLeadsSync,
    
    CatalogSettings: CatalogSettings,
    
    AuditLogs: AuditLogs,
    
    Calls: Calls,
    
    ContactNotesImport: ContactNotesImport,
    
    DealNotesImport: DealNotesImport,
    
    Reports: Reports,
    SalesReport: SalesReport,
    
}

function _getCurrentPage(url) {
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }
    let urlLastPart = url.split('/').pop();
    if (urlLastPart.includes('?')) {
        urlLastPart = urlLastPart.split('?')[0];
    }

    // Normalize kebab-case (e.g., lead-detail) to match PascalCase keys (LeadDetail)
    const normalized = urlLastPart.replace(/-/g, '').toLowerCase();
    const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === normalized);
    return pageName || Object.keys(PAGES)[0];
}

// Create a wrapper component that uses useLocation inside the Router context
function PagesContent() {
    const location = useLocation();
    const currentPage = _getCurrentPage(location.pathname);
    
    return (
        <>
            <Routes>
                {/* Public route */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                {import.meta.env.DEV ? <Route path="/test-mobile" element={<MobilePreview />} /> : null}
                {import.meta.env.DEV ? <Route path="/stage/mobile" element={<StageMobileAccess />} /> : null}
                {import.meta.env.DEV ? <Route path="/stage/exit" element={<StageExit />} /> : null}

                {/* Dedicated mobile routes based on desktop screens (iterative adjustment) */}
                <Route path="/m/*" element={
                    <ProtectedRoute>
                        <MobileUIProvider>
                            <MobileRoutes />
                        </MobileUIProvider>
                    </ProtectedRoute>
                } />
                
                {/* Public meeting room */}
                <Route path="/meet/:meetingId" element={<MeetRoom />} />
                
                {/* SDR Dashboard - fullscreen without Layout, public with code verification */}
                <Route path="/sdr-dashboard" element={<SDRDashboard />} />
                
                {/* Consultant Dashboard - fullscreen without Layout, public with code verification */}
                <Route path="/consultant-dashboard" element={<ConsultantDashboard />} />
                
                {/* Admin route - must be authenticated AND admin */}
                <Route path="/admin" element={
                    <ProtectedRoute>
                        <AdminRoute>
                            <Layout currentPageName="Admin">
                                <Admin />
                            </Layout>
                        </AdminRoute>
                    </ProtectedRoute>
                } />
                
                {/* Admin Merge Contacts route */}
                <Route path="/admin/merge-contacts" element={
                    <ProtectedRoute>
                        <AdminRoute>
                            <Layout currentPageName="MergeContacts">
                                <MergeContacts />
                            </Layout>
                        </AdminRoute>
                    </ProtectedRoute>
                } />
                
                {/* Admin Merge Deals route */}
                <Route path="/admin/merge-deals" element={
                    <ProtectedRoute>
                        <AdminRoute>
                            <Layout currentPageName="MergeDeals">
                                <MergeDeals />
                            </Layout>
                        </AdminRoute>
                    </ProtectedRoute>
                } />
                
                
                {/* Admin Quality Analysis route */}
                <Route path="/admin/quality-analysis" element={
                    <ProtectedRoute>
                        <AdminRoute>
                            <Layout currentPageName="QualityAnalysisRunner">
                                <QualityAnalysisRunner />
                            </Layout>
                        </AdminRoute>
                    </ProtectedRoute>
                } />

                {/* Admin Power Dialer route */}
                <Route path="/admin/power-dialer" element={
                    <ProtectedRoute>
                        <AdminRoute>
                            <Layout currentPageName="PowerDialerDashboard">
                                <PowerDialerDashboard />
                            </Layout>
                        </AdminRoute>
                    </ProtectedRoute>
                } />

                {/* Admin Meeting Health route */}
                <Route path="/admin/meeting-health" element={
                    <ProtectedRoute>
                        <AdminRoute>
                            <Layout currentPageName="MeetingHealthDashboard">
                                <MeetingHealthDashboard />
                            </Layout>
                        </AdminRoute>
                    </ProtectedRoute>
                } />
                
                {/* Protected routes */}
                <Route path="/*" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <Routes>
                                <Route path="/" element={<Home />} />
                                <Route path="/home" element={<Home />} />
                                <Route path="/dashboard" element={<MyDashboard />} />
                                <Route path="/Dashboard" element={<MyDashboard />} />
                                <Route path="/Cases" element={<Cases />} />
                                <Route path="/CaseDetail" element={<CaseDetail />} />
                                {/* Legacy Settings redirect */}
                                <Route path="/Settings" element={<Navigate to="/settings/team" replace />} />
                                <Route path="/DashboardSettings" element={<DashboardSettings />} />
                                <Route path="/Profile" element={<Profile />} />
                                <Route path="/UserDetail" element={<UserDetail />} />
                                <Route path="/Leads" element={<Leads />} />
                                <Route path="/LeadDetail" element={<LeadDetail />} />
                                {/* Alias in kebab-case for opening in new tab or external links */}
                                <Route path="/lead-detail" element={<LeadDetail />} />
                                <Route path="/CustomerDetail" element={<CustomerDetail />} />
                                <Route path="/JotformIntegration" element={<JotformIntegration />} />
                                <Route path="/outbound" element={<OutboundPanel />} />
                                <Route path="/outbound/discador/sessoes" element={<DialerSessions />} />
                                <Route path="/outbound/discador/sessao/:id" element={<DialerSessionResult />} />
                <Route path="/listas" element={<Lists />} />
                <Route path="/listas/:id" element={<ListDetail />} />
                                <Route path="/meu-painel" element={<Navigate to="/dashboard" replace />} />
                                <Route path="/agenda" element={<Agenda />} />
                                <Route path="/Contacts" element={<Contacts />} />
                                <Route path="/ContactDetail" element={<ContactDetail />} />
                                {/* Alias in kebab-case for opening in new tab or external links */}
                                <Route path="/contact-detail" element={<ContactDetail />} />
                                <Route path="/Deals" element={<Deals />} />
                                <Route path="/DealDetail" element={<DealDetail />} />
                                {/* Alias in kebab-case for opening in new tab or external links */}
                                <Route path="/deal-detail" element={<DealDetail />} />
                                <Route path="/deals/:id" element={<DealDetail />} />
                                <Route path="/QuickSetupCaseTypes" element={<QuickSetupCaseTypes />} />
                                <Route path="/CasesServicesSettings" element={<CasesServicesSettings />} />
                                <Route path="/Tasks" element={<TasksRedirect />} />
                <Route path="/WhatsApp" element={<WhatsApp />} />
                <Route path="/whatsapp-connect" element={<WhatsAppConnect />} />
        <Route path="/product-feedback" element={<ProductFeedback />} />
        <Route path="/bug-reports" element={<ProductFeedback />} /> {/* Redirect old route */}
                <Route path="/ZohoImport" element={<ZohoImport />} />
                <Route path="/FacebookLeadsMigration" element={
                  <AdminRoute>
                    <FacebookLeadsMigration />
                  </AdminRoute>
                } />
                <Route path="/FacebookLeadsSync" element={
                  <AdminRoute>
                    <FacebookLeadsSync />
                  </AdminRoute>
                } />
                <Route path="/FacebookSettings" element={
                  <AdminRoute>
                    <FacebookSettings />
                  </AdminRoute>
                } />
                <Route path="/facebook-settings" element={
                  <AdminRoute>
                    <FacebookSettings />
                  </AdminRoute>
                } />
                <Route path="/calls" element={<Calls />} />
                <Route path="/ContactNotesImport" element={
                  <AdminRoute>
                    <ContactNotesImport />
                  </AdminRoute>
                } />
                <Route path="/DealNotesImport" element={
                  <AdminRoute>
                    <DealNotesImport />
                  </AdminRoute>
                } />
                <Route path="/kommo-import" element={
                  <AdminRoute>
                    <KommoImport />
                  </AdminRoute>
                } />
                <Route path="/kommo-sync" element={
                  <AdminRoute>
                    <KommoSync />
                  </AdminRoute>
                } />
                <Route path="/admin/kommo-bulk-import" element={
                  <AdminRoute>
                    <AdminKommoImport />
                  </AdminRoute>
                } />
                {/* Knowledge Base routes */}
                <Route path="/knowledge-base" element={<KnowledgeBase />} />
                <Route path="/knowledge-base/new" element={<KnowledgeBase mode="create" />} />
                <Route path="/knowledge-base/edit/:slug" element={<KnowledgeBase mode="edit" />} />
                <Route path="/knowledge-base/:slug" element={<KnowledgeBase />} />
                
                {/* Reports */}
                <Route path="/reports" element={<Reports />} />
                <Route path="/reports/sales-report" element={<SalesReport />} />
                <Route path="/reports/commission-report" element={<CommissionReport />} />
                <Route path="/reports/processos-report" element={<ProcessosReport />} />
                
                {/* Announcements */}
                <Route path="/announcements" element={<Announcements />} />
                
                {/* Onboarding */}
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/onboarding/detail" element={<OnboardingDetail />} />
                
                {/* Customer Service */}
                <Route path="/customer-service" element={
                  <CustomerServiceRoute>
                    <CustomerService />
                  </CustomerServiceRoute>
                } />
                <Route path="/customer-service/dashboard" element={
                  <AdminRoute>
                    <ManagerDashboard />
                  </AdminRoute>
                } />
                
                {/* Service Detail */}
                <Route path="/ServiceDetail" element={<ServiceDetail />} />
                <Route path="/service-detail" element={<ServiceDetail />} />
                
                {/* IT Backlog - Admin Only */}
                <Route path="/it-backlog" element={
                  <AdminRoute>
                    <ItBacklog />
                  </AdminRoute>
                } />
                
                {/* Performance Dashboard - Admin Only */}
                <Route path="/admin/performance" element={
                  <AdminRoute>
                    <PerformanceDashboard />
                  </AdminRoute>
                } />
                
                {/* Settings Module with nested routes */}
                <Route path="/settings/*" element={
                  <Suspense fallback={<div className="p-6">Carregando...</div>}>
                    <SettingsLayout />
                  </Suspense>
                }>
                  <Route index element={<Navigate to="/settings/team" replace />} />
                  
                  {/* Team & Access Category */}
                  <Route path="team" element={<TeamSettings />} />
                  <Route path="team/users" element={<UserManagement />} />
                  <Route path="team/departments" element={<DepartmentsSettings />} />
                  <Route path="team/roles" element={<SystemRolesSettings />} />
                  <Route path="team/sales-goals" element={<SalesGoalsSettings />} />
                  <Route path="team/dashboard" element={<DashboardSettings />} />
                  <Route path="team/new-collaborator" element={<NewCollaboratorOnboarding />} />
                  
                  {/* Communication Category */}
                  <Route path="communication" element={<CommunicationSettings />} />
                  <Route path="communication/whatsapp" element={<WhatsAppSettings />} />
                  <Route path="communication/templates" element={<WhatsAppMessageTemplatesSettings />} />
                  <Route path="communication/templates/new" element={<WhatsAppTemplateDetail />} />
                  <Route path="communication/templates/:id" element={<WhatsAppTemplateDetail />} />
                  <Route path="communication/video-ring" element={<VideoRingSettings />} />
                  <Route path="communication/call-ring" element={<CallRingSettings />} />
                  <Route path="communication/phone-numbers" element={<CompanyPhoneNumbers />} />
                  <Route path="communication/company-call-ring" element={<CompanyCallRingSettings />} />
                  <Route path="communication/power-dialer" element={<PowerDialerSettings />} />
                  <Route path="communication/notifications" element={<NotificationSettings />} />
                  <Route path="communication/announcements" element={<AnnouncementsSettings />} />
                  <Route path="communication/connection-corrections" element={<ConnectionCorrectionSettings />} />
                  
                  {/* Operations Category */}
                  <Route path="operations" element={<OperationsSettings />} />
                  <Route path="operations/assignment-rules" element={<AssignmentRulesSettings />} />
                  <Route path="operations/reactivation" element={<ReactivationSettings />} />
                  <Route path="operations/referral" element={<ReferralRequestSettings />} />
                  <Route path="operations/ticket-templates" element={<TicketTemplatesSettings />} />
                  <Route path="operations/milestones" element={<MilestoneTemplatesSettings />} />
                  <Route path="operations/sla-config" element={<SLAConfigSettings />} />
                  <Route path="operations/webhooks" element={<WebhookManagement />} />
                  
                  {/* Cases & Services Category */}
                  <Route path="cases" element={<CasesSettings />} />
                  <Route path="cases/catalog" element={<CatalogSettings />} />
                  <Route path="cases/roles" element={<SetupCaseRoles />} />
                  <Route path="cases/forms" element={<FormTemplatesSettings />} />
                  <Route path="cases/qualification-options" element={<QualificationOptionsSettings />} />
                  
                  {/* Financial Category */}
                  <Route path="financial" element={<FinancialSettings />} />
                  
                  {/* Integrations Category */}
                  <Route path="integrations" element={<IntegrationsSettings />} />
                  <Route path="integrations/zoho" element={<ZohoMapping />} />
                  <Route path="integrations/suvsign" element={<SuvSignSettings />} />
                  
                  {/* System Category */}
                  <Route path="system" element={<SystemSettings />} />
                  <Route path="system/company" element={<CompanySettings />} />
                  <Route path="system/modules" element={<ModuleSettings />} />
                  <Route path="system/supabase" element={<SetupSupabase />} />
                  <Route path="system/data-backup" element={<DataSettings />} />
                  <Route path="system/audit-logs" element={<AuditLogs />} />
                  <Route path="system/security" element={<SecuritySettings />} />
                  <Route path="system/personalization" element={<UIPersonalizationSettings />} />
                </Route>
                
                {/* Legacy Settings URL redirects for backward compatibility */}
                <Route path="/UserManagement" element={<Navigate to="/settings/team/users" replace />} />
                <Route path="/DepartmentsSettings" element={<Navigate to="/settings/team/departments" replace />} />
                <Route path="/SystemRolesSettings" element={<Navigate to="/settings/team/roles" replace />} />
                <Route path="/SalesGoalsSettings" element={<Navigate to="/settings/team/sales-goals" replace />} />
                <Route path="/DashboardSettings" element={<Navigate to="/settings/team/dashboard" replace />} />
                <Route path="/WhatsAppSettings" element={<Navigate to="/settings/communication/whatsapp" replace />} />
                <Route path="/WhatsAppMessageTemplatesSettings" element={<Navigate to="/settings/communication/templates" replace />} />
                <Route path="/VideoRingSettings" element={<Navigate to="/settings/communication/video-ring" replace />} />
                <Route path="/videoringsettings" element={<Navigate to="/settings/communication/video-ring" replace />} />
                <Route path="/CallRingSettings" element={<Navigate to="/settings/communication/call-ring" replace />} />
                <Route path="/callringsettings" element={<Navigate to="/settings/communication/call-ring" replace />} />
                <Route path="/NotificationSettings" element={<Navigate to="/settings/communication/notifications" replace />} />
                <Route path="/settings/announcements" element={<Navigate to="/settings/communication/announcements" replace />} />
                <Route path="/AssignmentRulesSettings" element={<Navigate to="/settings/operations/assignment-rules" replace />} />
                <Route path="/ReactivationSettings" element={<Navigate to="/settings/operations/reactivation" replace />} />
                <Route path="/ReferralRequestSettings" element={<Navigate to="/settings/operations/referral" replace />} />
                <Route path="/TicketTemplatesSettings" element={<Navigate to="/settings/operations/ticket-templates" replace />} />
                <Route path="/MilestoneTemplatesSettings" element={<Navigate to="/settings/operations/milestones" replace />} />
                <Route path="/CatalogSettings" element={<Navigate to="/settings/cases/catalog" replace />} />
                <Route path="/catalogsettings" element={<Navigate to="/settings/cases/catalog" replace />} />
                <Route path="/SetupCaseRoles" element={<Navigate to="/settings/cases/roles" replace />} />
                <Route path="/FormTemplatesSettings" element={<Navigate to="/settings/cases/forms" replace />} />
                <Route path="/ZohoMapping" element={<Navigate to="/settings/integrations/zoho" replace />} />
                <Route path="/SuvSignSettings" element={<Navigate to="/settings/integrations/suvsign" replace />} />
                <Route path="/CompanySettings" element={<Navigate to="/settings/system/company" replace />} />
                <Route path="/ModuleSettings" element={<Navigate to="/settings/system/modules" replace />} />
                <Route path="/SetupSupabase" element={<Navigate to="/settings/system/supabase" replace />} />
                <Route path="/DataSettings" element={<Navigate to="/settings/system/data-backup" replace />} />
                <Route path="/audit-logs" element={<Navigate to="/settings/system/audit-logs" replace />} />
                <Route path="/SecuritySettings" element={<Navigate to="/settings/system/security" replace />} />
                <Route path="/UIPersonalizationSettings" element={<Navigate to="/settings/system/personalization" replace />} />

                {/* Finance Module */}
                <Route path="/finance" element={<FinanceDashboard />} />
                <Route path="/finance/cash-flow" element={<CashFlow />} />
                <Route path="/finance/bank-accounts" element={<BankAccounts />} />
                <Route path="/finance/bank-accounts/:accountId/transactions" element={<BankAccountTransactions />} />
                <Route path="/finance/receivables" element={<Receivables />} />
                <Route path="/finance/reconciliation" element={<Reconciliation />} />
                <Route path="/finance/close" element={<ClosePeriod />} />
                <Route path="/finance/collection" element={<CollectionDashboard />} />
                <Route path="/finance/expenses" element={<Expenses />} />
                <Route path="/finance/reports" element={<FinanceReportsMenu />} />
                <Route path="/finance/reports/dre" element={<DreReport />} />
                <Route path="/finance/reports/weekly-receivables" element={<WeeklyReceivablesReport />} />
                <Route path="/finance/reports/cohort-analysis" element={<CohortAnalysisReport />} />
                <Route path="/finance/reports/seller-performance" element={<SellerPerformanceReport />} />
                <Route path="/finance/reports/blueviza-conversions" element={<BlueVizaConversionsReport />} />
                <Route path="/finance/reports/payment-audit" element={<PaymentAuditReport />} />
                <Route path="/finance/transactions" element={<TransactionsReport />} />
                <Route path="/finance/reports/transactions" element={<Navigate to="/finance/transactions" replace />} />
                <Route path="/finance/stripe" element={<StripeMirror />} />
                <Route path="/finance/dre" element={<DreReport />} />
                <Route path="/finance/budget" element={<Budget />} />
                <Route path="/finance/budget-vs-actual" element={<BudgetVsActual />} />
                <Route path="/finance/settings" element={<FinanceSettings />} />
                <Route path="/finance/settings/employees" element={<Navigate to="/people/employees" replace />} />
                <Route path="/finance/settings/recurring" element={<RecurringExpenses />} />
                <Route path="/finance/settings/kpis" element={<KpiTargets />} />
                <Route path="/finance/settings/chart-of-accounts" element={<ChartOfAccounts />} />
                <Route path="/finance/settings/exchange-rates" element={<ExchangeRates />} />
                <Route path="/finance/settings/cost-analysis" element={<CostAnalysis />} />
                <Route path="/finance/settings/stripe-cleanup" element={<StripeCleanup />} />
                <Route path="/finance/settings/commission-rules" element={<CommissionRules />} />
                <Route path="/finance/comissoes" element={<CommissionRules />} />
                
                {/* People Module */}
                <Route path="/people" element={<PeopleDashboard />} />
                <Route path="/people/employees" element={<EmployeesPage />} />
                            </Routes>
                        </Layout>
                    </ProtectedRoute>
                } />
            </Routes>
        </>
    );
}

export default function Pages() {
    return (
        <Router>
            <PagesContent />
            <PowerDialerGlobalConsole />
            <PWAUpdatePrompt />
        </Router>
    );
}
